(() => {
  'use strict';

  let savedSliderStates = null;

  function parsePriceNumber(value) {
    let s = String(value ?? '').trim();
    if (!s) return 0;

    s = s.replace(/[^\d.,-]/g, '');
    const lastComma = s.lastIndexOf(',');
    const lastDot = s.lastIndexOf('.');

    if (lastComma > -1 && lastDot > -1) {
      if (lastComma > lastDot) {
        s = s.replace(/\./g, '').replace(',', '.');
      } else {
        s = s.replace(/,/g, '');
      }
    } else if (lastComma > -1) {
      const parts = s.split(',');
      if (parts.length === 2 && parts[1].length <= 2) {
        s = `${parts[0].replace(/\./g, '')}.${parts[1]}`;
      } else {
        s = s.replace(/,/g, '');
      }
    } else if (lastDot > -1 && s.split('.').length > 2) {
      s = s.replace(/\./g, '');
    }

    const number = Number(s);
    return Number.isFinite(number) && number >= 0 ? number : 0;
  }

  function getSliderKey(slider) {
    return slider.closest('.js-filter')?.id || slider.closest('price-range')?.id || '';
  }

  function readSliderState(slider) {
    const textInputs = slider.querySelectorAll('.field__input');
    const minRange = slider.querySelector('[data-role="min"]');
    const maxRange = slider.querySelector('[data-role="max"]');

    return {
      rangeMax: slider.dataset.rangeMax || '',
      minText: textInputs[0]?.value ?? '',
      maxText: textInputs[1]?.value ?? '',
      minRange: minRange?.value ?? '',
      maxRange: maxRange?.value ?? '',
    };
  }

  function applySliderState(slider, state) {
    if (!state) return;

    const textInputs = slider.querySelectorAll('.field__input');
    const minRange = slider.querySelector('[data-role="min"]');
    const maxRange = slider.querySelector('[data-role="max"]');

    if (textInputs[0]) textInputs[0].value = state.minText ?? '';
    if (textInputs[1]) textInputs[1].value = state.maxText ?? '';

    if (minRange && state.minRange !== '') minRange.value = state.minRange;
    if (maxRange && state.maxRange !== '') maxRange.value = state.maxRange;
  }

  function getRangeMax(slider, maxRange, textInputs) {
    const fromDataset = parsePriceNumber(slider.dataset.rangeMax);
    if (fromDataset > 0) {
      slider._gtRangeMax = fromDataset;
      return fromDataset;
    }

    const fromRangeAttr = parsePriceNumber(maxRange?.max);
    if (fromRangeAttr > 0) {
      slider.dataset.rangeMax = String(Math.round(fromRangeAttr));
      slider._gtRangeMax = fromRangeAttr;
      return fromRangeAttr;
    }

    const fromTextMax = parsePriceNumber(textInputs?.[1]?.getAttribute('data-max'));
    if (fromTextMax > 0) {
      slider.dataset.rangeMax = String(Math.round(fromTextMax));
      slider._gtRangeMax = fromTextMax;
      return fromTextMax;
    }

    return 0;
  }

  function clampTextInputsToCap(slider, maxCap) {
    const textInputs = slider.querySelectorAll('.field__input');
    if (textInputs.length < 2 || maxCap <= 0) return;

    if (textInputs[0].value !== '') {
      const minVal = Math.min(parsePriceNumber(textInputs[0].value), maxCap);
      textInputs[0].value = minVal <= 0 ? '' : String(Math.round(minVal));
      textInputs[0].setAttribute('data-max', String(Math.round(maxCap)));
    }

    if (textInputs[1].value !== '') {
      const maxVal = Math.min(parsePriceNumber(textInputs[1].value), maxCap);
      textInputs[1].value = maxVal >= maxCap ? '' : String(Math.round(maxVal));
    }

    textInputs[1].setAttribute('data-max', String(Math.round(maxCap)));
  }

  function ensureRangeAttributes(slider, maxCap) {
    if (maxCap <= 0) return false;

    const minRange = slider.querySelector('[data-role="min"]');
    const maxRange = slider.querySelector('[data-role="max"]');
    const cap = String(Math.round(maxCap));

    slider.dataset.rangeMax = cap;
    slider._gtRangeMax = maxCap;

    if (minRange) {
      minRange.min = '0';
      minRange.max = cap;
    }

    if (maxRange) {
      maxRange.min = '0';
      maxRange.max = cap;
    }

    return true;
  }

  function setTrack(slider, minValue, maxValue, rangeMax) {
    const range = slider.querySelector('.gt-price-slider__range');
    if (!range || rangeMax <= 0) return;

    const left = (minValue / rangeMax) * 100;
    const right = (maxValue / rangeMax) * 100;

    range.style.left = `${left}%`;
    range.style.width = `${Math.max(right - left, 0)}%`;
  }

  function triggerFacetSubmit(slider) {
    const form = slider.closest('form');
    if (!form) return;
    form.dispatchEvent(new Event('input', { bubbles: true }));
  }

  function syncFromTextInputs(slider) {
    const textInputs = slider.querySelectorAll('.field__input');
    const minRange = slider.querySelector('[data-role="min"]');
    const maxRange = slider.querySelector('[data-role="max"]');
    if (textInputs.length < 2 || !minRange || !maxRange) return;

    const maxCap = getRangeMax(slider, maxRange, textInputs);
    if (!ensureRangeAttributes(slider, maxCap)) return;

    const rawMin = textInputs[0].value === '' ? 0 : parsePriceNumber(textInputs[0].value);
    const hasMaxFilter = textInputs[1].value !== '';
    const rawMax = hasMaxFilter ? parsePriceNumber(textInputs[1].value) : maxCap;

    const minValue = Math.min(Math.max(rawMin, 0), maxCap);
    let maxValue = Math.max(Math.min(rawMax, maxCap), minValue);

    if (!hasMaxFilter) {
      maxValue = maxCap;
    }

    clampTextInputsToCap(slider, maxCap);

    minRange.value = String(Math.round(minValue));
    maxRange.value = String(Math.round(maxValue));
    setTrack(slider, minValue, maxValue, maxCap);
  }

  function syncFromRanges(slider, changedRole) {
    const textInputs = slider.querySelectorAll('.field__input');
    const minRange = slider.querySelector('[data-role="min"]');
    const maxRange = slider.querySelector('[data-role="max"]');
    if (textInputs.length < 2 || !minRange || !maxRange) return;

    const maxCap = getRangeMax(slider, maxRange, textInputs);
    if (!ensureRangeAttributes(slider, maxCap)) return;

    let minValue = Math.min(parsePriceNumber(minRange.value), maxCap);
    let maxValue = Math.min(parsePriceNumber(maxRange.value), maxCap);

    if (minValue > maxValue) {
      if (changedRole === 'min') {
        maxValue = minValue;
        maxRange.value = String(Math.round(maxValue));
      } else {
        minValue = maxValue;
        minRange.value = String(Math.round(minValue));
      }
    }

    textInputs[0].value = minValue <= 0 ? '' : String(Math.round(minValue));
    textInputs[1].value = maxValue >= maxCap ? '' : String(Math.round(maxValue));
    clampTextInputsToCap(slider, maxCap);
    setTrack(slider, minValue, maxValue, maxCap);
  }

  function sourcePriceInputsEmpty(sourceSlider) {
    const inputs = sourceSlider?.querySelectorAll?.('.field__input');
    if (!inputs || inputs.length < 2) return true;
    return inputs[0].value.trim() === '' && inputs[1].value.trim() === '';
  }

  function clearSavedSliderStates() {
    savedSliderStates = null;
  }

  function syncSliderFromSource(targetSlider, sourceSlider) {
    const targetInputs = targetSlider.querySelectorAll('.field__input');
    const sourceInputs = sourceSlider.querySelectorAll('.field__input');
    const sourceMax = parsePriceNumber(sourceSlider.dataset.rangeMax);

    if (sourceMax > 0) {
      targetSlider.dataset.rangeMax = String(Math.round(sourceMax));
      targetSlider._gtRangeMax = sourceMax;
    }

    if (sourceInputs[0] && targetInputs[0]) {
      targetInputs[0].value = sourceInputs[0].value;
    }

    if (sourceInputs[1] && targetInputs[1]) {
      targetInputs[1].value = sourceInputs[1].value;
    }
  }

  function setActiveRangeInput(slider, activeInput) {
    const minRange = slider.querySelector('[data-role="min"]');
    const maxRange = slider.querySelector('[data-role="max"]');
    if (!minRange || !maxRange) return;

    minRange.classList.remove('is-active');
    maxRange.classList.remove('is-active');
    if (activeInput) activeInput.classList.add('is-active');
  }

  function bindRangeFocus(slider) {
    const minRange = slider.querySelector('[data-role="min"]');
    const maxRange = slider.querySelector('[data-role="max"]');
    if (!minRange || !maxRange) return;

    const activate = (input) => {
      setActiveRangeInput(slider, input);
    };

    const deactivate = () => {
      minRange.classList.remove('is-active');
      maxRange.classList.remove('is-active');
    };

    [minRange, maxRange].forEach((input) => {
      input.addEventListener('pointerdown', () => activate(input));
      input.addEventListener('focus', () => activate(input));
    });

    slider.addEventListener('pointerup', deactivate);
    slider.addEventListener('pointercancel', deactivate);
  }

  function bindSlider(slider) {
    if (!(slider instanceof HTMLElement)) return;

    if (slider.dataset.gtSliderBound !== 'true') {
      slider.dataset.gtSliderBound = 'true';
      bindRangeFocus(slider);

      slider.addEventListener('input', (event) => {
        const target = event.target;
        if (!(target instanceof HTMLInputElement)) return;

        if (target.matches('.gt-price-slider__range-input')) {
          const role = target.getAttribute('data-role') || 'min';
          syncFromRanges(slider, role);
        } else if (target.matches('.field__input')) {
          syncFromTextInputs(slider);
        }
      });

      slider.addEventListener(
        'blur',
        (event) => {
          const target = event.target;
          if (!(target instanceof HTMLInputElement) || !target.matches('.field__input')) return;
          syncFromTextInputs(slider);
        },
        true
      );

      slider.addEventListener('change', (event) => {
        const target = event.target;
        if (!(target instanceof HTMLInputElement)) return;
        if (!target.matches('.gt-price-slider__range-input')) return;
        triggerFacetSubmit(slider);
      });
    }

    syncFromTextInputs(slider);
  }

  function refreshAll(root = document) {
    requestAnimationFrame(() => {
      root.querySelectorAll('[data-gt-price-slider]').forEach(bindSlider);
    });
  }

  function saveAllPriceSliderStates() {
    const map = {};
    document.querySelectorAll('[data-gt-price-slider]').forEach((slider) => {
      const key = getSliderKey(slider);
      if (!key) return;
      map[key] = readSliderState(slider);
    });
    savedSliderStates = map;
  }

  function restoreAllPriceSliderStates() {
    if (!savedSliderStates) return;

    document.querySelectorAll('[data-gt-price-slider]').forEach((slider) => {
      const key = getSliderKey(slider);
      const state = savedSliderStates[key];
      if (!state) return;

      const textInputs = slider.querySelectorAll('.field__input');
      const isCleared = textInputs[0]?.value.trim() === '' && textInputs[1]?.value.trim() === '';
      const savedHadFilter = state.minText.trim() !== '' || state.maxText.trim() !== '';

      if (isCleared && savedHadFilter) return;

      const minRange = slider.querySelector('[data-role="min"]');
      const maxRange = slider.querySelector('[data-role="max"]');
      const capAfterRender = getRangeMax(slider, maxRange, textInputs);
      const savedMax = parsePriceNumber(state.maxRange);
      const savedMin = parsePriceNumber(state.minRange);
      const currentMax = parsePriceNumber(maxRange?.value);
      const currentMin = parsePriceNumber(minRange?.value);
      const thumbsReset =
        capAfterRender > 0 &&
        savedMax > 0 &&
        currentMax <= 0 &&
        currentMin <= 0 &&
        (savedMax > 0 || savedMin > 0);

      if (capAfterRender <= 0 || thumbsReset) {
        applySliderState(slider, state);
      }
    });
  }

  function syncRangeMaxFromSource(targetSlider, sourceSlider) {
    if (!targetSlider || !sourceSlider) return false;

    const sourceCap = parsePriceNumber(sourceSlider.dataset.rangeMax);
    if (sourceCap <= 0) return false;

    targetSlider.dataset.rangeMax = String(Math.round(sourceCap));
    delete targetSlider._gtRangeMax;

    const textInputs = targetSlider.querySelectorAll('.field__input');
    textInputs.forEach((input) => {
      input.setAttribute('data-max', String(Math.round(sourceCap)));
    });

    return true;
  }

  function updatePriceSliderFromFacetSource(sourceRoot, targetRoot) {
    const sourceSlider = sourceRoot?.querySelector?.('[data-gt-price-slider]');
    const targetSlider = targetRoot?.querySelector?.('[data-gt-price-slider]');
    if (!sourceSlider || !targetSlider) return;

    syncRangeMaxFromSource(targetSlider, sourceSlider);
    bindSlider(targetSlider);
  }

  function preservePriceSliderFacet(current, source) {
    const targetSlider = current.querySelector('[data-gt-price-slider]');
    const sourceSlider = source.querySelector('[data-gt-price-slider]');
    if (!targetSlider || !sourceSlider) return false;

    if (sourcePriceInputsEmpty(sourceSlider)) {
      return false;
    }

    const savedState = readSliderState(targetSlider);

    const targetSummary = current.querySelector('.facets__summary');
    const sourceSummary = source.querySelector('.facets__summary');
    if (targetSummary && sourceSummary) {
      targetSummary.outerHTML = sourceSummary.outerHTML;
    }

    const targetHeader = current.querySelector('.facets__header-vertical, .facets__header');
    const sourceHeader = source.querySelector('.facets__header-vertical, .facets__header');
    if (targetHeader && sourceHeader) {
      targetHeader.outerHTML = sourceHeader.outerHTML;
    }

    syncRangeMaxFromSource(targetSlider, sourceSlider);
    syncSliderFromSource(targetSlider, sourceSlider);
    applySliderState(targetSlider, savedState);
    bindSlider(targetSlider);
    const maxCap = getRangeMax(
      targetSlider,
      targetSlider.querySelector('[data-role="max"]'),
      targetSlider.querySelectorAll('.field__input')
    );
    if (maxCap > 0) clampTextInputsToCap(targetSlider, maxCap);
    return true;
  }

  window.gtSavePriceSliderStates = saveAllPriceSliderStates;
  window.gtRestorePriceSliderStates = restoreAllPriceSliderStates;
  window.gtClearPriceSliderSavedStates = clearSavedSliderStates;
  window.gtPreservePriceSliderFacet = preservePriceSliderFacet;
  window.gtUpdatePriceSliderFromFacetSource = updatePriceSliderFromFacetSource;
  window.initGtPriceRangeSliders = refreshAll;
  window.refreshGtPriceRangeSliders = refreshAll;

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => refreshAll());
  } else {
    refreshAll();
  }

  document.addEventListener('shopify:section:load', (event) => {
    const target = event.target;
    if (!(target instanceof HTMLElement)) return;
    refreshAll(target);
  });

  document.addEventListener('gt:facets:rendered', () => refreshAll());
})();
